/**
 * useDailyScrum — the React binding for the Daily Scrum view (/team/daily-scrum).
 *
 * Strict layering: the page calls THIS hook; the hook calls
 * {@link TaskUpdateStore} (the standup updates) + {@link TaskStore} (to resolve
 * task titles and to know which tasks are currently blocked). The stores call
 * the api facade; the page never imports a store or the api directly.
 *
 * The scrum is a pure PROJECTION of the room's task updates: for each
 * (engineer, task) pair we keep the newest update. The SAME {@link TaskUpdate}
 * that appears on a task's Updates section powers this roll-up — one write, two
 * surfaces.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { useActiveRoom } from '../rooms/RoomContext';
import { TaskUpdateStore } from './stores/taskUpdateStore';
import { TaskStore } from '../tasks/taskStore';
import {
  groupUpdatesByEngineer,
  type ScrumEngineerGroup,
} from './scrum/dailyScrumModel';
import type { TaskUpdate } from './models/types';

export interface UseDailyScrumResult {
  /** The room the scrum is scoped to, or null when none is active. */
  roomId: string | null;
  /** The room's task updates (raw, newest-first-ish from the store). */
  updates: TaskUpdate[];
  /** Updates grouped by engineer → task (latest per pair). */
  groups: ScrumEngineerGroup[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useDailyScrum(): UseDailyScrumResult {
  const api = useApi();
  const { activeRoomId } = useActiveRoom();
  const stores = useMemo(
    () => ({ updates: new TaskUpdateStore(api), tasks: new TaskStore(api) }),
    [api],
  );

  const [updates, setUpdates] = useState<TaskUpdate[]>([]);
  const [taskTitles, setTaskTitles] = useState<Map<string, string>>(new Map());
  const [blockedTasks, setBlockedTasks] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!activeRoomId) {
      setUpdates([]);
      setTaskTitles(new Map());
      setBlockedTasks(new Set());
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [rows, roomTasks] = await Promise.all([
        stores.updates.listByRoom(activeRoomId),
        stores.tasks.listByRoom(activeRoomId),
      ]);
      setUpdates(rows);
      setTaskTitles(new Map(roomTasks.map((t) => [t.id, t.title])));
      setBlockedTasks(
        new Set(roomTasks.filter((t) => t.blocked).map((t) => t.id)),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load the daily scrum.');
    } finally {
      setLoading(false);
    }
  }, [activeRoomId, stores]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const groups = useMemo(
    () => groupUpdatesByEngineer(updates, taskTitles, blockedTasks),
    [updates, taskTitles, blockedTasks],
  );

  return { roomId: activeRoomId, updates, groups, loading, error, refresh };
}
